import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  InputLabel,
  TableRow,
  MenuItem,
  TablePagination,
  FormControl,
  TextField,
  IconButton,
  Select,
  Box,
  Alert,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios'; 
import Swal from 'sweetalert2'; 

// Project import
import { gridSpacing } from 'config.js';

// Updated API URLs
const apiUrl = 'https://apidashboard.eurekagroup.id/office';
const addOfficeUrl = `${apiUrl}/add`;
const editOfficeUrl = `${apiUrl}/edit`;
const deleteOfficeUrl = (id) => `${apiUrl}/delete/${id}`;

const OfficePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successPopup, setSuccessPopup] = useState(false);
  const [message, setMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [officeCode, setOfficeCode] = useState('');
  const [officeName, setOfficeName] = useState('');
  const [severity, setSeverity] = useState('success'); 
  const [isActive, setIsActive] = useState(1);

  // Fetch office data on component mount
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        setData(response.data); // Set the fetched data to the state
      } catch (error) {
        console.error('Error fetching office data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOffices();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (office) => {
    if (office) {
      setSelectedOffice(office);
      setOfficeCode(office.office_code);
      setOfficeName(office.office_name);
      setIsActive(office.is_active);
    } else {
      setSelectedOffice(null);
      setOfficeCode('');
      setOfficeName('');
      setIsActive(1);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveOffice = async () => {
    // Validation: Check if all required fields are filled
    if (!officeCode || !officeName || isActive === '') {
      setMessage('All fields are required!');
      setSeverity('error');
      setSuccessPopup(true);
      return;
    }
  
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    const officeData = { office_code: officeCode, office_name: officeName, is_active: isActive };
  
    if (selectedOffice) {
      // Editing an existing office
      try {
        await axios.put(editOfficeUrl, { ...officeData, id: selectedOffice._id }, {
          headers: {
            Authorization: `Bearer ${token}`, // Menambahkan Authorization header
          },
        });
        setData(data.map(emp => (emp._id === selectedOffice._id ? { ...emp, ...officeData } : emp)));
        setMessage('Office updated successfully');
        setSeverity('success');
      } catch (error) {
        console.error('Error updating office:', error);
        setMessage('An error occurred while updating the office.');
        setSeverity('error');
      }
    } else {
      // Adding a new office
      try {
        const response = await axios.post(addOfficeUrl, officeData, {
          headers: {
            Authorization: `Bearer ${token}`, // Menambahkan Authorization header
          },
        });
        setData([...data, response.data]);
        setMessage('Office added successfully');
        setSeverity('success');
      } catch (error) {
        console.error('Error adding office:', error);
        setMessage('An error occurred while adding the office.');
        setSeverity('error');
      }
    }
  
    setSuccessPopup(true);
    handleCloseDialog();
  };  

  const handleDeleteClick = async (id) => {
    console.log(`Delete clicked for ID: ${id}`); // Add this log
  
    const token = localStorage.getItem('token'); // Ambil token dari localStorage
  
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
  
    if (result.isConfirmed) {
      try {
        await axios.delete(deleteOfficeUrl(id), {
          headers: {
            Authorization: `Bearer ${token}`, // Menambahkan Authorization header
          },
        });
        setData(data.filter(office => office._id !== id));
        setMessage('Office deleted successfully');
        setSuccessPopup(true);
      } catch (error) {
        console.error('Error deleting office:', error);
      }
    }
  };  

  const filteredData = data.filter(office =>
    office.office_name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
            <CardHeader
              title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
                  <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen Kantor
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data kantor dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
                      variant="outlined"
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={() => window.location.reload()}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog(null)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                      sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah Kantor
                    </Button>
                </Box>
              }
        sx={{ pb: 2 }}
            />
      <Divider />
            <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari Kantor"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Kantor
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data kantor...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        No
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Kode Kantor
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Nama Kantor
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((office, index) => (
                      <TableRow 
                        key={office._id}
                        sx={{ 
                          '&:hover': { 
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                            transform: 'scale(1.001)',
                            transition: 'all 0.2s ease'
                          },
                          '&:nth-of-type(even)': {
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {index + 1 + page * rowsPerPage}
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {office.office_code}
                          </Typography>
                          </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {office.office_name}
                          </Typography>
                          </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: office.is_active === 1 ? 'success.light' : 'error.light',
                            color: office.is_active === 1 ? 'success.dark' : 'error.dark',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {office.is_active === 1 ? 'Aktif' : 'Tidak Aktif'}
                          </Box>
                          </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton 
                              onClick={() => handleOpenDialog(office)} 
                              sx={{ 
                                color: 'info.main',
                                '&:hover': { 
                                  bgcolor: 'info.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              onClick={() => handleDeleteClick(office._id)} 
                              sx={{ 
                                color: 'error.main',
                                '&:hover': { 
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

                <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
                />
            </>
          )}
              </Box>
            </CardContent>
          </Card>
    </div>

      {/* Success/Failure Popup */}
      <Snackbar
        open={successPopup}
        autoHideDuration={3000}
        onClose={() => setSuccessPopup(false)}
      >
        <Alert onClose={() => setSuccessPopup(false)} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>

      {/* Office Dialog Form */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{selectedOffice ? 'Edit Office' : 'Add Office'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
          <Grid item xs={12} sx={{ marginTop: 2 }}>
          <TextField
            label="Office Code"
            variant="outlined"
            fullWidth
            value={officeCode}
            onChange={(e) => setOfficeCode(e.target.value)}
          />
        </Grid>
            <Grid item xs={12}>
              <TextField
                label="Office Name"
                variant="outlined"
                fullWidth
                value={officeName}
                onChange={(e) => setOfficeName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value)}
                  label="Status"
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveOffice}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OfficePage;
